<template>
  <Teleport to="body">
    <div v-if="isVisible" class="rt-overlay" :class="{ dark: isDark }">
      <div class="rt-modal">

        <!-- Header -->
        <div class="rt-header">
          <div>
            <div class="rt-eyebrow">Reporte de Turno</div>
            <h2 class="rt-title" v-if="data">
              {{ fmtFecha(data.arqueo.periodo_inicio) }}
              <span v-if="data.arqueo.periodo_inicio !== data.arqueo.periodo_fin">
                — {{ fmtFecha(data.arqueo.periodo_fin) }}
              </span>
            </h2>
          </div>
          <button class="rt-btn-cerrar-x" @click="$emit('close')">✕</button>
        </div>

        <div v-if="cargando" class="rt-empty">
          <div class="rt-spinner"></div>
          <span>Generando reporte...</span>
        </div>

        <template v-else-if="data">
          <!-- Info del turno -->
          <div class="rt-info-row">
            <div class="rt-info-item">
              <span class="rt-info-lbl">Cajero auditado</span>
              <span class="rt-info-val">{{ nombreCompleto(data.cajero) }}</span>
            </div>
            <div class="rt-info-item">
              <span class="rt-info-lbl">Auditado por</span>
              <span class="rt-info-val">{{ nombreCompleto(data.auditor) }}</span>
            </div>
            <div class="rt-info-item">
              <span class="rt-info-lbl">Transacciones</span>
              <span class="rt-info-val">{{ data.resumen.totalTransacciones }}</span>
            </div>
            <div class="rt-info-item">
              <span class="rt-info-lbl">Total ventas</span>
              <span class="rt-info-val">L. {{ fmt(data.resumen.totalVentas) }}</span>
            </div>
          </div>

          <!-- Breakdown por método de pago -->
          <div class="rt-section-title">Ventas por método de pago</div>
          <div class="rt-metodos">
            <div
              v-for="m in data.metricas"
              :key="m.metodo"
              class="rt-metodo-card"
              :class="metodoClass(m.metodo)"
            >
              <div class="rt-metodo-header">
                <i :class="metodoIcon(m.metodo)"></i>
                <span class="rt-metodo-nombre">{{ m.metodo }}</span>
                <span class="rt-metodo-pct">{{ m.porcentaje }}%</span>
              </div>
              <div class="rt-metodo-total">L. {{ fmt(m.total) }}</div>
              <div class="rt-metodo-tx">{{ m.transacciones }} transacciones</div>
              <!-- mini barra de porcentaje -->
              <div class="rt-bar-bg">
                <div class="rt-bar-fill" :style="{ width: m.porcentaje + '%' }"></div>
              </div>
            </div>
          </div>

          <!-- Cuadre de caja -->
          <div class="rt-section-title">Cuadre de caja</div>
          <div class="rt-cuadre">
            <div class="rt-cuadre-row">
              <span>Monto de apertura</span>
              <span>L. {{ fmt(data.arqueo.monto_apertura) }}</span>
            </div>
            <div class="rt-cuadre-row">
              <span>Ventas en efectivo</span>
              <span>L. {{ fmt(metricaMetodo('Efectivo').total) }}</span>
            </div>
            <div class="rt-cuadre-row rt-cuadre-sep">
              <span>Total esperado en caja</span>
              <span class="rt-cuadre-esperado">L. {{ fmt(data.arqueo.esperado) }}</span>
            </div>
            <div class="rt-cuadre-row">
              <span>Total contado</span>
              <span>L. {{ fmt(data.arqueo.total_contado) }}</span>
            </div>
            <div class="rt-cuadre-row rt-cuadre-dif" :class="difClass">
              <span>Diferencia</span>
              <strong>L. {{ fmt(data.arqueo.diferencia) }}</strong>
            </div>
          </div>

          <div v-if="data.arqueo.diferencia === 0" class="rt-result rt-result-ok">
            <i class="bi bi-check-circle-fill"></i> Cuadre perfecto — sin diferencias
          </div>
          <div v-else-if="data.arqueo.diferencia > 0" class="rt-result rt-result-warning">
            <i class="bi bi-exclamation-triangle-fill"></i> Sobrante de L. {{ fmt(Math.abs(data.arqueo.diferencia)) }}
          </div>
          <div v-else class="rt-result rt-result-danger">
            <i class="bi bi-x-circle-fill"></i> Faltante de L. {{ fmt(Math.abs(data.arqueo.diferencia)) }}
          </div>

          <!-- Notas -->
          <div v-if="notasTexto" class="rt-notas">
            <div class="rt-section-title">Notas</div>
            <p>{{ notasTexto }}</p>
          </div>

          <!-- Footer -->
          <div class="rt-footer">
            <button class="rt-btn-cerrar" @click="$emit('close')">Cerrar</button>
            <button class="rt-btn-pdf" @click="exportarPDF">
              <i class="bi bi-file-earmark-pdf-fill"></i> Exportar PDF
            </button>
          </div>
        </template>

      </div>
    </div>
  </Teleport>
</template>

<script>
import { getReporteTurno } from '../../services/featuresSolicitudes';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default {
  name: 'ReporteTurnoModal',
  props: {
    isVisible: { type: Boolean, required: true },
    arqueo:    { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return { isDark: false, cargando: false, data: null };
  },
  mounted() {
    this.isDark = localStorage.getItem('isDarkMode') === 'true';
    this._onToggleDark = () => { this.isDark = localStorage.getItem('isDarkMode') === 'true'; };
    window.addEventListener('poswave:toggle-dark', this._onToggleDark);
  },
  beforeUnmount() {
    window.removeEventListener('poswave:toggle-dark', this._onToggleDark);
  },
  computed: {
    difClass() {
      if (!this.data) return '';
      const d = this.data.arqueo.diferencia;
      if (d < 0) return 'dif-danger';
      if (d > 0) return 'dif-warning';
      return 'dif-ok';
    },
    notasTexto() {
      if (!this.data?.arqueo.notas) return '';
      try {
        const parsed = JSON.parse(this.data.arqueo.notas);
        return parsed.notas || '';
      } catch { return this.data.arqueo.notas; }
    },
  },
  watch: {
    isVisible(v) {
      if (v && this.arqueo) this.cargar();
      else this.data = null;
    },
  },
  methods: {
    async cargar() {
      this.cargando = true;
      try {
        this.data = await getReporteTurno(this.arqueo.id_arqueo);
      } catch (_e) {
        this.data = null;
      } finally {
        this.cargando = false;
      }
    },
    metricaMetodo(metodo) {
      return this.data?.metricas.find(m => m.metodo === metodo) || { total: 0, transacciones: 0 };
    },
    fmt(v) {
      return Number(v || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtFecha(f) {
      if (!f) return '—';
      return new Date(f + 'T00:00:00').toLocaleDateString('es-HN', { day: '2-digit', month: 'long', year: 'numeric' });
    },
    nombreCompleto(u) {
      if (!u) return '—';
      return `${u.nombre} ${u.apellido}`;
    },
    metodoClass(m) {
      const map = { Efectivo: 'mc-efectivo', Transferencia: 'mc-transfer', Credito: 'mc-credito', Mixto: 'mc-mixto' };
      return map[m] || 'mc-otro';
    },
    metodoIcon(m) {
      const map = { Efectivo: 'bi bi-cash-coin', Transferencia: 'bi bi-bank', Credito: 'bi bi-credit-card', Mixto: 'bi bi-shuffle' };
      return map[m] || 'bi bi-cash';
    },
    exportarPDF() {
      if (!this.data) return;
      const d   = this.data;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('REPORTE DE TURNO', 105, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Período: ${this.fmtFecha(d.arqueo.periodo_inicio)} — ${this.fmtFecha(d.arqueo.periodo_fin)}`, 15, 30);
      doc.text(`Cajero: ${this.nombreCompleto(d.cajero)}`, 15, 36);
      doc.text(`Auditado por: ${this.nombreCompleto(d.auditor)}`, 15, 42);
      doc.text(`Generado: ${new Date().toLocaleString('es-HN')}`, 15, 48);

      autoTable(doc, {
        startY: 54,
        head: [['Método de Pago', 'Transacciones', 'Total (L.)', '%']],
        body: d.metricas.map(m => [m.metodo, m.transacciones, this.fmt(m.total), m.porcentaje + '%']),
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
      });

      const afterTable = doc.lastAutoTable.finalY + 8;
      autoTable(doc, {
        startY: afterTable,
        head: [['Concepto', 'Monto (L.)']],
        body: [
          ['Monto de Apertura',     this.fmt(d.arqueo.monto_apertura)],
          ['Ventas en Efectivo',    this.fmt(this.metricaMetodo('Efectivo').total)],
          ['Total Esperado en Caja', this.fmt(d.arqueo.esperado)],
          ['Total Contado',         this.fmt(d.arqueo.total_contado)],
          ['Diferencia',            this.fmt(d.arqueo.diferencia)],
        ],
        theme: 'plain',
        columnStyles: { 1: { halign: 'right' } },
      });

      doc.save(`ReporteTurno_${d.arqueo.periodo_inicio}.pdf`);
    },
  },
};
</script>

<style scoped>
.rt-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.55);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 1200; padding: 24px 12px; overflow-y: auto;
}
.rt-modal {
  background: #fff; border-radius: 12px;
  width: 100%; max-width: 700px;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
  overflow: hidden; margin: auto;
}
.rt-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 22px 24px 16px; border-bottom: 1px solid #e9ecef; gap: 12px;
}
.rt-eyebrow { font-size: 11px; font-weight: 700; letter-spacing:.1em; text-transform:uppercase; color:#6c757d; margin-bottom:2px; }
.rt-title { font-size: 18px; font-weight: 700; color: #1a202c; margin: 0; }

/* Info row */
.rt-info-row {
  display: grid; grid-template-columns: repeat(4, 1fr);
  border-bottom: 1px solid #e9ecef;
}
.rt-info-item { padding: 14px 20px; border-right: 1px solid #e9ecef; }
.rt-info-item:last-child { border-right: none; }
.rt-info-lbl { display:block; font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:#6c757d; margin-bottom:3px; }
.rt-info-val { font-size:14px; font-weight:600; color:#1a202c; }

/* Métodos */
.rt-section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#6c757d; padding:14px 24px 8px; }
.rt-metodos { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; padding:0 24px 16px; }
.rt-metodo-card { border-radius:8px; padding:14px 14px 10px; border:1px solid; }
.mc-efectivo  { background:#f0fdf4; border-color:#bbf7d0; }
.mc-transfer  { background:#eff6ff; border-color:#bfdbfe; }
.mc-credito   { background:#fffbeb; border-color:#fde68a; }
.mc-mixto     { background:#f5f3ff; border-color:#ddd6fe; }
.mc-otro      { background:#f9fafb; border-color:#e5e7eb; }
.rt-metodo-header { display:flex; align-items:center; gap:6px; margin-bottom:8px; }
.rt-metodo-nombre { font-size:12px; font-weight:600; flex:1; }
.rt-metodo-pct { font-size:11px; color:#6c757d; }
.rt-metodo-total { font-size:16px; font-weight:700; font-variant-numeric:tabular-nums; color:#1a202c; }
.rt-metodo-tx { font-size:11px; color:#6c757d; margin-top:2px; margin-bottom:8px; }
.rt-bar-bg { height:4px; background:#e5e7eb; border-radius:2px; }
.rt-bar-fill { height:4px; background:#3b82f6; border-radius:2px; transition:width .4s; }
.mc-efectivo .rt-bar-fill  { background:#16a34a; }
.mc-transfer .rt-bar-fill  { background:#2563eb; }
.mc-credito  .rt-bar-fill  { background:#d97706; }
.mc-mixto    .rt-bar-fill  { background:#7c3aed; }

/* Cuadre */
.rt-cuadre { padding:0 24px 16px; display:flex; flex-direction:column; gap:6px; }
.rt-cuadre-row { display:flex; justify-content:space-between; font-size:13.5px; color:#374151; }
.rt-cuadre-sep { border-top:1px solid #e9ecef; padding-top:8px; margin-top:4px; font-weight:600; }
.rt-cuadre-esperado { color:#1a202c; }
.rt-cuadre-dif { border-top:2px solid #e9ecef; padding-top:8px; margin-top:6px; font-size:15px; }
.dif-ok      { color:#16a34a; }
.dif-warning { color:#d97706; }
.dif-danger  { color:#dc2626; }

/* Resultado */
.rt-result { margin:0 24px 16px; padding:12px 16px; border-radius:8px; font-size:13px; font-weight:600; display:flex; align-items:center; gap:8px; }
.rt-result-ok      { background:#f0fdf4; color:#166534; }
.rt-result-warning { background:#fffbeb; color:#92400e; }
.rt-result-danger  { background:#fef2f2; color:#991b1b; }

/* Notas */
.rt-notas { padding:0 24px 16px; }
.rt-notas p { font-size:13px; color:#374151; }

/* Footer */
.rt-footer { padding:16px 24px; border-top:1px solid #e9ecef; display:flex; justify-content:flex-end; }
.rt-btn-pdf {
  background:#198754; color:#fff; border:none; border-radius:7px;
  padding:9px 18px; font-size:13px; font-weight:600; cursor:pointer;
  display:flex; align-items:center; gap:7px; min-width:auto;
  transition:background .15s;
}
.rt-btn-pdf:hover { background:#146c43; }

/* Botón cerrar X en header */
.rt-btn-cerrar-x {
  background: none; border: none; cursor: pointer;
  font-size: 1.2rem; color: #6c757d; padding: 6px 10px;
  border-radius: 6px; display: flex; align-items: center;
  line-height: 1; flex-shrink: 0;
}
.rt-btn-cerrar-x:hover { background: #f1f1f1; color: #212529; }

/* Botón cerrar en footer */
.rt-btn-cerrar {
  background: #6c757d; color: #fff; border: none; border-radius: 7px;
  padding: 9px 18px; font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 7px;
  transition: background .15s; margin-right: 8px;
}
.rt-btn-cerrar:hover { background: #545b62; }

/* Loading */
.rt-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:48px 24px; color:#6c757d; }
.rt-spinner { width:28px; height:28px; border:3px solid #e9ecef; border-top-color:#3b82f6; border-radius:50%; animation:spin .8s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* Dark mode */
.dark .rt-modal { background:#2d2d2d; color:#ffffff; }
.dark .rt-header { border-color:#2d3748; }
.dark .rt-eyebrow { color:#718096; }
.dark .rt-title { color:#e2e8f0; }
.dark .rt-info-row { border-color:#2d3748; }
.dark .rt-info-item { border-color:#2d3748; }
.dark .rt-info-lbl { color:#718096; }
.dark .rt-info-val { color:#e2e8f0; }
.dark .rt-section-title { color:#718096; }
.dark .rt-metodo-card { border-color:#2d3748; }
.dark .mc-efectivo { background:#052e16; } .dark .mc-transfer { background:#172040; }
.dark .mc-credito  { background:#1c1500; } .dark .mc-mixto    { background:#1e1b4b; }
.dark .rt-metodo-nombre { color:#e2e8f0; }
.dark .rt-metodo-total  { color:#e2e8f0; }
.dark .rt-metodo-tx     { color:#718096; }
.dark .rt-bar-bg { background:#2d3748; }
.dark .rt-cuadre-row { color:#cbd5e0; }
.dark .rt-cuadre-sep { border-color:#2d3748; }
.dark .rt-cuadre-dif { border-color:#2d3748; }
.dark .rt-result-ok      { background:#052e16; color:#4ade80; }
.dark .rt-result-warning { background:#1c1500; color:#fcd34d; }
.dark .rt-result-danger  { background:#1f0606; color:#f87171; }
.dark .rt-footer { border-color:#2d3748; }
.dark .rt-notas p { color:#cbd5e0; }
.dark .rt-btn-cerrar-x { color:#9e9e9e; }
.dark .rt-btn-cerrar-x:hover { background:#3d3d3d; color:#e5e5e5; }
.dark .rt-btn-cerrar { background:#4a4a4a; }
.dark .rt-btn-cerrar:hover { background:#5a5a5a; }
.dark .rt-btn-pdf { background:#198754; }
.dark .rt-btn-pdf:hover { background:#146c43; }

@media (max-width: 600px) {
  .rt-metodos { grid-template-columns: repeat(2,1fr); }
  .rt-info-row { grid-template-columns: repeat(2,1fr); }
}
</style>
